import { useState, useEffect } from 'react';
import { C } from '../styles/colours.js';
import { QUESTION_BANK, getSavedGames, saveGame, deleteSavedGame, generateAIQuestions, getText, getCategoryName, getSubcategoryName } from '../questionBank.js';
import { T, LANGUAGES } from '../translations.js';
import { supabase } from '../supabase.js';

const LATEST_T = {
  en: { latestTab: '🆕 Latest', loading: 'Loading...', noPacksYet: 'No quiz packs available yet. Check back soon!', questions: 'questions', today: 'Today', yesterday: 'Yesterday', daysAgo: 'days ago', weeksAgo: 'weeks ago', monthsAgo: 'months ago', NEW: 'NEW', FEATURED: '⭐ FEATURED', startThisPack: 'Start this pack', addToQuiz: 'Add all', publishedLabel: 'Published', emptyPack: 'This pack has no questions yet.', filterAll: 'All', filterFeatured: '⭐ Featured', filterNew: '🆕 New', noMatch: 'No packs match this filter.', searchPlaceholder: '🔍 Search packs...', loadMore: 'Load More', showing: 'Showing', of: 'of', selected: 'selected', selectAll: 'Select all', deselectAll: 'Deselect all', addSelected: 'Add selected', pickSpecific: 'Or pick specific questions', quickAdd: 'Quick add (random)' },
  fr: { latestTab: '🆕 Nouveautés', loading: 'Chargement...', noPacksYet: "Aucun pack disponible pour l'instant.", questions: 'questions', today: "Aujourd'hui", yesterday: 'Hier', daysAgo: 'jours', weeksAgo: 'semaines', monthsAgo: 'mois', NEW: 'NOUVEAU', FEATURED: '⭐ EN VEDETTE', startThisPack: 'Lancer ce pack', addToQuiz: 'Tout ajouter', publishedLabel: 'Publié', emptyPack: "Ce pack ne contient aucune question.", filterAll: 'Tous', filterFeatured: '⭐ En vedette', filterNew: '🆕 Nouveau', noMatch: 'Aucun pack ne correspond.', searchPlaceholder: '🔍 Rechercher des packs...', loadMore: 'Charger plus', showing: 'Affiche', of: 'sur', selected: 'sélectionnées', selectAll: 'Tout sélectionner', deselectAll: 'Tout désélectionner', addSelected: 'Ajouter la sélection', pickSpecific: 'Ou choisir des questions précises', quickAdd: 'Ajout rapide (aléatoire)' },
  ar: { latestTab: '🆕 الأحدث', loading: 'جار التحميل...', noPacksYet: 'لا توجد حزم مسابقات متاحة حتى الآن.', questions: 'سؤال', today: 'اليوم', yesterday: 'أمس', daysAgo: 'أيام', weeksAgo: 'أسابيع', monthsAgo: 'أشهر', NEW: 'جديد', FEATURED: '⭐ مميز', startThisPack: 'ابدأ هذه الحزمة', addToQuiz: 'أضف الكل', publishedLabel: 'نشر', emptyPack: 'هذه الحزمة لا تحتوي على أسئلة بعد.', filterAll: 'الكل', filterFeatured: '⭐ مميز', filterNew: '🆕 جديد', noMatch: 'لا توجد حزم مطابقة.', searchPlaceholder: '🔍 البحث عن الحزم...', loadMore: 'تحميل المزيد', showing: 'عرض', of: 'من', selected: 'محددة', selectAll: 'تحديد الكل', deselectAll: 'إلغاء التحديد', addSelected: 'أضف المحدد', pickSpecific: 'أو اختر أسئلة محددة', quickAdd: 'إضافة سريعة (عشوائي)' },
  es: { latestTab: '🆕 Lo último', loading: 'Cargando...', noPacksYet: 'No hay packs disponibles aún.', questions: 'preguntas', today: 'Hoy', yesterday: 'Ayer', daysAgo: 'días', weeksAgo: 'semanas', monthsAgo: 'meses', NEW: 'NUEVO', FEATURED: '⭐ DESTACADO', startThisPack: 'Iniciar este pack', addToQuiz: 'Añadir todo', publishedLabel: 'Publicado', emptyPack: 'Este pack aún no tiene preguntas.', filterAll: 'Todos', filterFeatured: '⭐ Destacado', filterNew: '🆕 Nuevo', noMatch: 'Ningún pack coincide.', searchPlaceholder: '🔍 Buscar packs...', loadMore: 'Cargar más', showing: 'Mostrando', of: 'de', selected: 'seleccionadas', selectAll: 'Seleccionar todo', deselectAll: 'Deseleccionar todo', addSelected: 'Añadir seleccionadas', pickSpecific: 'O elige preguntas específicas', quickAdd: 'Añadir rápido (aleatorio)' },
};

const PACK_COLORS = {
  gold: '#FFD166',
  red: '#EF476F',
  green: '#06D6A0',
  blue: '#3B82F6',
  purple: '#8B5CF6',
};

const PAGE_SIZE = 10;

function CreateGame({ onBack, onStart, draftQuestions, setDraftQuestions, draftGameName, setDraftGameName, lang = 'en' }) {
  const t = T[lang] || T.en;
  const lt = LATEST_T[lang] || LATEST_T.en;
  const isRTL = lang === 'ar';

  const [tab, setTab] = useState(() => {
    if (draftQuestions && draftQuestions.length > 0) return 'create';
    return 'latest';
  });
  const [gameName, setGameName] = useState(draftGameName || '');
  const [timer, setTimer] = useState(15);
  const [pts, setPts] = useState(100);
  const [questions, setQuestions] = useState(draftQuestions || []);
  const [q, setQ] = useState('');
  const [a, setA] = useState('');
  const [editIndex, setEditIndex] = useState(null);
  const [selectedCat, setSelectedCat] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [howMany, setHowMany] = useState(10);
  const [savedGames, setSavedGames] = useState(getSavedGames());
  const [aiTopic, setAiTopic] = useState('');
  const [aiCount, setAiCount] = useState(10);
  const [aiGenerated, setAiGenerated] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);

  const [showLangPicker, setShowLangPicker] = useState(false);
  const [displayLanguages, setDisplayLanguages] = useState([lang]);
  const [pendingConfig, setPendingConfig] = useState(null);

  const [latestPacks, setLatestPacks] = useState([]);
  const [packsLoading, setPacksLoading] = useState(true);
  const [selectedPack, setSelectedPack] = useState(null);
  const [selectedPackQuestions, setSelectedPackQuestions] = useState([]);
  const [packQuestionsLoading, setPackQuestionsLoading] = useState(false);
  const [packFilter, setPackFilter] = useState('all');
  const [packSearch, setPackSearch] = useState('');
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const [searchedPackIds, setSearchedPackIds] = useState(null);

  // Pick-and-mix selections (arrays of indices)
  const [pickPack, setPickPack] = useState([]);
  const [pickCat, setPickCat] = useState([]);

  useEffect(() => {
    loadLatestPacks();
  }, []);

  // iOS PWA fix: re-fetch packs when the app is reopened / brought back to foreground.
  // Installed iOS PWAs resume from a frozen state instead of reloading, so the one-time
  // mount fetch above never re-runs. This listens for the app becoming visible again
  // (and for window focus) and reloads the latest packs — no force-close needed.
  useEffect(() => {
    const refetchIfVisible = () => {
      if (document.visibilityState === 'visible') {
        loadLatestPacks();
      }
    };
    document.addEventListener('visibilitychange', refetchIfVisible);
    window.addEventListener('focus', refetchIfVisible);
    return () => {
      document.removeEventListener('visibilitychange', refetchIfVisible);
      window.removeEventListener('focus', refetchIfVisible);
    };
  }, []);

  // Reset pagination when filter or search changes
  useEffect(() => {
    setDisplayCount(PAGE_SIZE);
  }, [packFilter, packSearch]);

  // Reset picks when context changes
  useEffect(() => { setPickPack([]); }, [selectedPack]);
  useEffect(() => { setPickCat([]); }, [selectedSub]);

  // Search questions in Supabase when user types
  useEffect(() => {
    if (!packSearch.trim()) {
      setSearchedPackIds(null);
      return;
    }
    const timer = setTimeout(async () => {
      const query = packSearch.trim();
      const { data: matchingQs } = await supabase
        .from('questions')
        .select('id')
        .or(`q_en.ilike.%${query}%,q_fr.ilike.%${query}%,q_ar.ilike.%${query}%,q_es.ilike.%${query}%,a_en.ilike.%${query}%,a_fr.ilike.%${query}%,a_ar.ilike.%${query}%,a_es.ilike.%${query}%`);
      if (!matchingQs || matchingQs.length === 0) {
        setSearchedPackIds([]);
        return;
      }
      const qIds = matchingQs.map(q => q.id);
      const { data: junctions } = await supabase
        .from('quiz_pack_questions')
        .select('pack_id')
        .in('question_id', qIds);
      const packIds = [...new Set((junctions || []).map(j => j.pack_id))];
      setSearchedPackIds(packIds);
    }, 300);
    return () => clearTimeout(timer);
  }, [packSearch]);

  const loadLatestPacks = async () => {
    setPacksLoading(true);
    const nowIso = new Date().toISOString();
    const { data: packsData } = await supabase
      .from('quiz_packs')
      .select('*')
      .lte('published_at', nowIso)
      .order('published_at', { ascending: false });

    const { data: junction } = await supabase
      .from('quiz_pack_questions')
      .select('pack_id');

    const counts = {};
    (junction || []).forEach(j => {
      counts[j.pack_id] = (counts[j.pack_id] || 0) + 1;
    });

    setLatestPacks((packsData || []).map(p => ({ ...p, qCount: counts[p.id] || 0 })));
    setPacksLoading(false);
  };

  const handleSelectPack = async (pack) => {
    setSelectedPack(pack);
    setPickPack([]);
    setPackQuestionsLoading(true);

    const { data: junction } = await supabase
      .from('quiz_pack_questions')
      .select('question_id, position')
      .eq('pack_id', pack.id)
      .order('position', { ascending: true });

    if (!junction || junction.length === 0) {
      setSelectedPackQuestions([]);
      setPackQuestionsLoading(false);
      return;
    }

    const ids = junction.map(j => j.question_id);
    const { data: questionRows } = await supabase
      .from('questions')
      .select('*')
      .in('id', ids);

    const ordered = ids.map(id => questionRows.find(qr => qr.id === id)).filter(Boolean);
    const formatted = ordered.map(row => ({
      flag: row.flag || null,
      q: { en: row.q_en || '', fr: row.q_fr || row.q_en || '', ar: row.q_ar || row.q_en || '', es: row.q_es || row.q_en || '' },
      a: { en: row.a_en || '', fr: row.a_fr || row.a_en || '', ar: row.a_ar || row.a_en || '', es: row.a_es || row.a_en || '' },
    }));

    setSelectedPackQuestions(formatted);
    setPackQuestionsLoading(false);
  };

  const packLocalName = (p) => p[`name_${lang}`] || p.name_en;
  const packLocalDescription = (p) => p[`description_${lang}`] || p.description_en;

  const formatRelativeDate = (iso) => {
    if (!iso) return '';
    const today = new Date(); today.setHours(0,0,0,0);
    const pack = new Date(iso); pack.setHours(0,0,0,0);
    const diffDays = Math.round((today - pack) / 86400000);
    if (diffDays <= 0) return lt.today;
    if (diffDays === 1) return lt.yesterday;
    if (diffDays < 30) return `${diffDays} ${lt.daysAgo}`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} ${lt.monthsAgo}`;
    return pack.toLocaleDateString();
  };

  const isPackNew = (iso) => {
    if (!iso) return false;
    const today = new Date(); today.setHours(0,0,0,0);
    const pack = new Date(iso); pack.setHours(0,0,0,0);
    return Math.round((today - pack) / 86400000) <= 0;
  };

  const filteredPacks = latestPacks.filter(p => {
    if (packFilter === 'featured' && !p.featured) return false;
    if (packFilter === 'new' && !isPackNew(p.published_at)) return false;
    if (packSearch.trim()) {
      const query = packSearch.trim().toLowerCase();
      const nameMatch = [p.name_en, p.name_fr, p.name_ar, p.name_es]
        .filter(Boolean).join(' ').toLowerCase().includes(query);
      const questionMatch = searchedPackIds && searchedPackIds.includes(p.id);
      if (!nameMatch && !questionMatch) return false;
    }
    return true;
  });

  const visiblePacks = filteredPacks.slice(0, displayCount);
  const hasMore = displayCount < filteredPacks.length;

  const startPack = () => {
    if (!selectedPack || selectedPackQuestions.length === 0) return;
    handleStart({ gameName: packLocalName(selectedPack), timer, pts, questions: selectedPackQuestions });
  };

  const addPackToCurrentQuiz = () => {
    if (!selectedPack || selectedPackQuestions.length === 0) return;
    updateQuestions([...questions, ...selectedPackQuestions]);
    setSelectedPack(null);
    setSelectedPackQuestions([]);
    setTab('create');
  };

  const addSelectedPackQuestions = () => {
    const picked = pickPack.map(i => selectedPackQuestions[i]).filter(Boolean);
    if (picked.length === 0) return;
    updateQuestions([...questions, ...picked]);
    setSelectedPack(null);
    setSelectedPackQuestions([]);
    setPickPack([]);
    setTab('create');
  };

  const addSelectedCatQuestions = () => {
    const allQs = QUESTION_BANK[selectedCat][selectedSub];
    const picked = pickCat.map(i => allQs[i]).filter(Boolean);
    if (picked.length === 0) return;
    updateQuestions([...questions, ...picked]);
    setSelectedCat(null);
    setSelectedSub(null);
    setPickCat([]);
    setTab('create');
  };

  const togglePick = (list, setList, i) => {
    setList(list.includes(i) ? list.filter(x => x !== i) : [...list, i]);
  };

  const inputStyle = { width: '100%', background: C.inputBg, border: `1px solid ${C.inputBorder}`, borderRadius: 10, padding: '12px 14px', fontSize: 14, color: C.white, outline: 'none', fontFamily: 'sans-serif', boxSizing: 'border-box' };

  const updateQuestions = (updated) => { setQuestions(updated); setDraftQuestions(updated); };

  const addQuestion = () => {
    if (!q.trim() || !a.trim()) return;
    if (editIndex !== null) {
      const updated = [...questions];
      updated[editIndex] = { q: q.trim(), a: a.trim() };
      updateQuestions(updated);
      setEditIndex(null);
    } else {
      updateQuestions([...questions, { q: q.trim(), a: a.trim() }]);
    }
    setQ(''); setA('');
  };

  const deleteQuestion = (i) => updateQuestions(questions.filter((_, idx) => idx !== i));

  const startEdit = (i) => {
    setQ(getText(questions[i].q, lang));
    setA(getText(questions[i].a, lang));
    setEditIndex(i);
  };

  const addFromBank = () => {
    const allQs = QUESTION_BANK[selectedCat][selectedSub];
    const shuffled = [...allQs].sort(() => Math.random() - 0.5);
    const picked = shuffled.slice(0, howMany);
    updateQuestions([...questions, ...picked]);
    setSelectedCat(null);
    setSelectedSub(null);
    setTab('create');
  };

  const handleGenerateAI = () => {
    if (!aiTopic.trim()) return;
    setAiLoading(true);
    setTimeout(() => {
      const generated = generateAIQuestions(aiTopic, aiCount);
      setAiGenerated(generated);
      setAiLoading(false);
    }, 1500);
  };

  const addAIToQuiz = () => {
    updateQuestions([...questions, ...aiGenerated]);
    setAiGenerated([]);
    setAiTopic('');
    setTab('create');
  };

  const handleSave = () => {
    if (questions.length === 0) return;
    saveGame({ gameName: gameName || 'Untitled Quiz', timer, pts, questions });
    setSavedGames(getSavedGames());
    alert(t.gameSavedAlert);
  };

  const loadSaved = (game) => {
    setGameName(game.gameName);
    setDraftGameName(game.gameName);
    setTimer(game.timer);
    setPts(game.pts);
    updateQuestions(game.questions);
    setTab('create');
  };

  const handleDelete = (id) => { deleteSavedGame(id); setSavedGames(getSavedGames()); };

  const handleStart = (config) => {
    setPendingConfig(config);
    setDisplayLanguages([lang]);
    setShowLangPicker(true);
  };

  const toggleLanguage = (code) => {
    if (displayLanguages.includes(code)) {
      if (displayLanguages.length > 1) setDisplayLanguages(displayLanguages.filter(c => c !== code));
    } else {
      setDisplayLanguages([...displayLanguages, code]);
    }
  };

  const confirmStart = () => {
    if (displayLanguages.length === 0) return;
    setShowLangPicker(false);
    onStart({ ...pendingConfig, displayLanguages });
    setPendingConfig(null);
  };

  // Reusable checkbox question row
  const pickRow = (item, i, list, setList) => {
    const checked = list.includes(i);
    return (
      <div key={i} onClick={() => togglePick(list, setList, i)} style={{ background: checked ? `${C.green}12` : C.card, border: `1px solid ${checked ? C.green : C.border}`, borderRadius: 12, padding: '12px 14px', marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
        <div style={{ fontSize: 18, flexShrink: 0, lineHeight: 1.2 }}>{checked ? '✅' : '⬜'}</div>
        {item.flag && (
          <img src={`https://flagcdn.com/w160/${item.flag}.png`} alt="" style={{ width: 44, borderRadius: 4, flexShrink: 0, marginTop: 2, border: '1px solid rgba(255,255,255,0.15)' }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, lineHeight: 1.4 }}>{getText(item.q, lang)}</div>
          <div style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>✅ {getText(item.a, lang)}</div>
        </div>
      </div>
    );
  };

  const selectAllBar = (allList, list, setList) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 700 }}>{list.length} {lt.selected}</div>
      <button onClick={() => setList(list.length === allList.length ? [] : allList.map((_, i) => i))} style={{ background: 'none', border: 'none', color: C.gold, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
        {list.length === allList.length ? lt.deselectAll : lt.selectAll}
      </button>
    </div>
  );

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ background: C.bg, minHeight: '100vh', fontFamily: 'sans-serif', color: C.white, padding: '20px', maxWidth: 480, margin: '0 auto' }}>

      {showLangPicker && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(7,7,26,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div dir={isRTL ? 'rtl' : 'ltr'} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: '24px 20px', maxWidth: 380, width: '100%', position: 'relative' }}>
            <button onClick={() => setShowLangPicker(false)} style={{ position: 'absolute', top: 12, [isRTL ? 'left' : 'right']: 12, background: 'rgba(255,255,255,0.06)', border: 'none', color: C.textSub, width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 16 }}>✕</button>
            <div style={{ fontSize: 18, fontWeight: 900, color: C.gold, marginBottom: 6, textAlign: 'center' }}>{t.chooseDisplayLanguages}</div>
            <div style={{ fontSize: 12, color: C.textSub, marginBottom: 18, textAlign: 'center', lineHeight: 1.5 }}>{t.displayLanguagesDesc}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
              {LANGUAGES.map(L => {
                const selected = displayLanguages.includes(L.code);
                return (
                  <button key={L.code} onClick={() => toggleLanguage(L.code)} style={{ background: selected ? `${C.gold}25` : C.inputBg, border: `2px solid ${selected ? C.gold : C.inputBorder}`, borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', color: C.white }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 22 }}>{L.flag}</span>
                      <span style={{ fontSize: 15, fontWeight: 700 }}>{L.label}</span>
                    </div>
                    <div style={{ fontSize: 18, color: selected ? C.gold : C.textMuted }}>{selected ? '✅' : '⬜'}</div>
                  </button>
                );
              })}
            </div>
            {displayLanguages.length === 0 && (<div style={{ fontSize: 12, color: C.red, textAlign: 'center', marginBottom: 10 }}>{t.selectAtLeastOne}</div>)}
            <button onClick={confirmStart} disabled={displayLanguages.length === 0} style={{ width: '100%', background: displayLanguages.length > 0 ? C.red : 'rgba(255,255,255,0.1)', color: displayLanguages.length > 0 ? C.white : C.textMuted, border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 800, cursor: displayLanguages.length > 0 ? 'pointer' : 'not-allowed' }}>{t.launchGame}</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <button onClick={onBack} style={{ background: C.card, border: 'none', color: C.textSub, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>{t.back}</button>
        <div style={{ fontSize: 11, letterSpacing: 2, color: C.gold, fontWeight: 700 }}>{t.createGameBadge}</div>
      </div>

      {questions.length > 0 && (
        <div style={{ background: `${C.green}15`, border: `1px solid ${C.green}30`, borderRadius: 12, padding: '10px 14px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setTab('create')}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>✅ {questions.length} {t.questionsReady}</div>
          <div style={{ fontSize: 11, color: C.textSub }}>{t.tapToReview}</div>
        </div>
      )}

      <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 4, display: 'flex', marginBottom: 20, overflowX: 'auto' }}>
        {[
          { id: 'latest', label: lt.latestTab },
          { id: 'saved', label: t.tabSaved },
          { id: 'create', label: t.tabCreate },
          { id: 'categories', label: t.tabCategories },
        ].map(tt => (
          <button key={tt.id} onClick={() => setTab(tt.id)} style={{ flex: 1, minWidth: 70, background: tab === tt.id ? C.gold : 'transparent', color: tab === tt.id ? '#000' : C.textSub, border: 'none', borderRadius: 8, padding: '9px 2px', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>{tt.label}</button>
        ))}
      </div>

      {tab === 'latest' && !selectedPack && (
        <div>
          {!packsLoading && latestPacks.length > 0 && (
            <>
              <input
                type="text"
                placeholder={lt.searchPlaceholder}
                value={packSearch}
                onChange={e => setPackSearch(e.target.value)}
                style={{ ...inputStyle, marginBottom: 10 }}
              />

              <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
                {[
                  { id: 'all', label: lt.filterAll },
                  { id: 'featured', label: lt.filterFeatured },
                  { id: 'new', label: lt.filterNew },
                ].map(f => (
                  <button key={f.id} onClick={() => setPackFilter(f.id)} style={{
                    flex: 1,
                    background: packFilter === f.id ? C.gold : C.inputBg,
                    color: packFilter === f.id ? '#000' : C.textSub,
                    border: `1px solid ${packFilter === f.id ? C.gold : C.inputBorder}`,
                    borderRadius: 10, padding: '8px 4px',
                    fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>{f.label}</button>
                ))}
              </div>
            </>
          )}

          {packsLoading && <div style={{ textAlign: 'center', padding: 30, color: C.textSub, fontSize: 13 }}>{lt.loading}</div>}
          {!packsLoading && latestPacks.length === 0 && (
            <div style={{ background: C.card, border: `1px dashed ${C.border}`, borderRadius: 14, padding: '32px 20px', textAlign: 'center', color: C.textMuted, fontSize: 13 }}>{lt.noPacksYet}</div>
          )}
          {!packsLoading && latestPacks.length > 0 && filteredPacks.length === 0 && (
            <div style={{ background: C.card, border: `1px dashed ${C.border}`, borderRadius: 14, padding: '32px 20px', textAlign: 'center', color: C.textMuted, fontSize: 13 }}>{lt.noMatch}</div>
          )}

          {!packsLoading && visiblePacks.map(p => {
            const themeC = PACK_COLORS[p.theme_color] || PACK_COLORS.gold;
            const isNew = isPackNew(p.published_at);
            return (
              <button key={p.id} onClick={() => handleSelectPack(p)} style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderLeft: `4px solid ${themeC}`, borderRadius: 14, padding: '16px', marginBottom: 12, cursor: 'pointer', textAlign: isRTL ? 'right' : 'left', color: C.white, display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ fontSize: 32, flexShrink: 0 }}>{p.emoji || '📋'}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 15, fontWeight: 800 }}>{packLocalName(p)}</div>
                    {p.featured && <span style={{ fontSize: 9, color: C.gold, fontWeight: 800, letterSpacing: 1 }}>{lt.FEATURED}</span>}
                    {isNew && !p.featured && <span style={{ fontSize: 9, color: C.green, fontWeight: 800, letterSpacing: 1, background: `${C.green}20`, padding: '2px 6px', borderRadius: 4 }}>{lt.NEW}</span>}
                  </div>
                  {packLocalDescription(p) && <div style={{ fontSize: 12, color: C.textSub, marginBottom: 4, lineHeight: 1.4 }}>{packLocalDescription(p)}</div>}
                  <div style={{ fontSize: 11, color: C.textMuted }}>{p.qCount} {lt.questions} · {formatRelativeDate(p.published_at)}</div>
                </div>
                <div style={{ color: C.textMuted, fontSize: 18 }}>→</div>
              </button>
            );
          })}

          {!packsLoading && filteredPacks.length > 0 && (
            <div style={{ textAlign: 'center', marginTop: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 12 }}>
                {lt.showing} {visiblePacks.length} {lt.of} {filteredPacks.length}
              </div>
              {hasMore && (
                <button onClick={() => setDisplayCount(displayCount + PAGE_SIZE)} style={{
                  background: C.inputBg,
                  border: `1px solid ${C.gold}`,
                  color: C.gold,
                  borderRadius: 12,
                  padding: '12px 24px',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}>
                  {lt.loadMore} ↓
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'latest' && selectedPack && (
        <div>
          <button onClick={() => { setSelectedPack(null); setSelectedPackQuestions([]); }} style={{ background: 'none', border: 'none', color: C.textSub, fontSize: 13, cursor: 'pointer', marginBottom: 16 }}>{t.back}</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
            <div style={{ fontSize: 44 }}>{selectedPack.emoji || '📋'}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 18, fontWeight: 900 }}>{packLocalName(selectedPack)}</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{selectedPackQuestions.length} {lt.questions} · {formatRelativeDate(selectedPack.published_at)}</div>
            </div>
          </div>
          {packLocalDescription(selectedPack) && <div style={{ fontSize: 13, color: C.textSub, marginBottom: 16, lineHeight: 1.5 }}>{packLocalDescription(selectedPack)}</div>}
          {packQuestionsLoading && <div style={{ textAlign: 'center', padding: 20, color: C.textSub, fontSize: 13 }}>{lt.loading}</div>}
          {!packQuestionsLoading && selectedPackQuestions.length === 0 && (
            <div style={{ background: C.card, border: `1px dashed ${C.border}`, borderRadius: 12, padding: '20px', textAlign: 'center', color: C.textMuted, fontSize: 13, marginBottom: 14 }}>{lt.emptyPack}</div>
          )}

         {!packQuestionsLoading && selectedPackQuestions.length > 0 && (
            <>
              {/* Action buttons — TOP */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
                {pickPack.length > 0 && (
                  <button onClick={addSelectedPackQuestions} style={{ width: '100%', background: C.green, color: '#000', border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>➕ {lt.addSelected} ({pickPack.length})</button>
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={addPackToCurrentQuiz} style={{ flex: 1, background: C.gold, color: '#000', border: 'none', borderRadius: 12, padding: '14px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>➕ {lt.addToQuiz}</button>
                  <button onClick={startPack} style={{ flex: 1, background: C.red, color: C.white, border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>▶ {lt.startThisPack}</button>
                </div>
              </div>

              {selectAllBar(selectedPackQuestions, pickPack, setPickPack)}
              {selectedPackQuestions.map((item, i) => pickRow(item, i, pickPack, setPickPack))}

              {/* Action buttons — BOTTOM */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                {pickPack.length > 0 && (
                  <button onClick={addSelectedPackQuestions} style={{ width: '100%', background: C.green, color: '#000', border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>➕ {lt.addSelected} ({pickPack.length})</button>
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={addPackToCurrentQuiz} style={{ flex: 1, background: C.gold, color: '#000', border: 'none', borderRadius: 12, padding: '14px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>➕ {lt.addToQuiz}</button>
                  <button onClick={startPack} style={{ flex: 1, background: C.red, color: C.white, border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>▶ {lt.startThisPack}</button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'saved' && (
        <div>
          <div style={{ fontSize: 13, color: C.textSub, marginBottom: 16 }}>{t.savedQuizzesDesc}</div>
          {savedGames.length === 0 && (
            <div style={{ background: C.card, border: `1px dashed ${C.border}`, borderRadius: 14, padding: '32px 20px', textAlign: 'center', color: C.textMuted, fontSize: 13 }}>{t.noSavedGames}<br />{t.createAndSave}</div>
          )}
          {savedGames.map(game => (
            <div key={game.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '14px 16px', marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{game.gameName}</div>
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{game.questions.length} {t.questionsCountLabel}</div>
                </div>
                <button onClick={() => handleDelete(game.id)} style={{ background: `${C.red}20`, border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 13 }}>🗑️</button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => loadSaved(game)} style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, color: C.white, borderRadius: 9, padding: '9px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{t.editBtn}</button>
                <button onClick={() => handleStart(game)} style={{ flex: 2, background: C.red, color: C.white, border: 'none', borderRadius: 9, padding: '9px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>{t.startNow}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'create' && (
        <div>
          {/* ⚡ Quick add shortcut */}
          <div style={{ background: `${C.gold}10`, border: `1px solid ${C.gold}35`, borderRadius: 14, padding: '14px 16px', marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.gold, marginBottom: 4 }}>⚡ Add questions fast</div>
            <div style={{ fontSize: 12, color: C.textSub, marginBottom: 12, lineHeight: 1.4 }}>Pick ready-made questions instead of typing every one.</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setTab('categories')} style={{ flex: 1, background: C.gold, color: '#000', border: 'none', borderRadius: 10, padding: '12px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>📚 From categories</button>
              <button onClick={() => setTab('latest')} style={{ flex: 1, background: C.inputBg, color: C.white, border: `1px solid ${C.gold}55`, borderRadius: 10, padding: '12px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>🆕 From packs</button>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.textMuted, marginBottom: 6 }}>{t.gameNameLabel}</div>
            <input type="text" placeholder={t.gameNamePlaceholder} value={gameName} onChange={e => { setGameName(e.target.value); setDraftGameName(e.target.value); }} style={inputStyle} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.textMuted, marginBottom: 8 }}>{t.pointsPerQuestion}</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {[50, 100, 200].map(p => (
                <button key={p} onClick={() => setPts(p)} style={{ flex: 1, background: pts === p ? C.gold : C.inputBg, color: pts === p ? '#000' : C.textSub, border: `1px solid ${pts === p ? C.gold : C.inputBorder}`, borderRadius: 8, padding: '8px 2px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>{p}</button>
              ))}
            </div>
          </div>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '16px', marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: editIndex !== null ? C.gold : C.white, marginBottom: 12 }}>{editIndex !== null ? `${t.editingQ}${editIndex + 1}` : t.addQuestionTitle}</div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.textMuted, marginBottom: 6 }}>{t.question}</div>
              <input type="text" placeholder={t.questionPlaceholder} value={q} onChange={e => setQ(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.textMuted, marginBottom: 6 }}>{t.correctAnswerInputLabel}</div>
              <input type="text" placeholder={t.answerPlaceholder} value={a} onChange={e => setA(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={addQuestion} style={{ flex: 1, background: editIndex !== null ? C.gold : C.green, color: '#000', border: 'none', borderRadius: 10, padding: '11px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>{editIndex !== null ? t.saveEdit : t.addBtn}</button>
              {editIndex !== null && (
                <button onClick={() => { setEditIndex(null); setQ(''); setA(''); }} style={{ background: C.inputBg, border: `1px solid ${C.inputBorder}`, color: C.textSub, borderRadius: 10, padding: '11px 16px', fontSize: 13, cursor: 'pointer' }}>{t.cancelBtn}</button>
              )}
            </div>
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: C.textMuted, marginBottom: 10 }}>{questions.length} {t.questionsPlural}</div>
          {questions.length === 0 && (
            <div style={{ background: C.card, border: `1px dashed ${C.border}`, borderRadius: 12, padding: '20px', textAlign: 'center', color: C.textMuted, fontSize: 13, marginBottom: 14 }}>{t.noQuestionsYet}</div>
          )}
          {questions.map((item, i) => (
            <div key={i} style={{ background: C.card, border: `1px solid ${editIndex === i ? C.gold : C.border}`, borderRadius: 12, padding: '12px 14px', marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: C.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: '#000', flexShrink: 0, marginTop: 2 }}>{i + 1}</div>
              {item.flag && (
                <img src={`https://flagcdn.com/w160/${item.flag}.png`} alt="" style={{ width: 44, borderRadius: 4, flexShrink: 0, marginTop: 2, border: '1px solid rgba(255,255,255,0.15)' }} />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, lineHeight: 1.4 }}>{getText(item.q, lang)}</div>
                <div style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>✅ {getText(item.a, lang)}</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => startEdit(i)} style={{ background: `${C.gold}20`, border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 13 }}>✏️</button>
                <button onClick={() => deleteQuestion(i)} style={{ background: `${C.red}20`, border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 13 }}>🗑️</button>
              </div>
            </div>
          ))}
          {questions.length > 0 && (
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button onClick={handleSave} style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, color: C.white, borderRadius: 12, padding: '14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{t.saveBtn}</button>
              <button onClick={() => handleStart({ gameName, timer, pts, questions })} style={{ flex: 2, background: C.red, color: C.white, border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>{t.startGameBtn}</button>
            </div>
          )}
        </div>
      )}

      {tab === 'categories' && !selectedCat && (
        <div>
          <div style={{ fontSize: 13, color: C.textSub, marginBottom: 16 }}>{t.chooseCategory}</div>
          {Object.keys(QUESTION_BANK).map(cat => (
            <button key={cat} onClick={() => setSelectedCat(cat)} style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px', cursor: 'pointer', textAlign: isRTL ? 'right' : 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, color: C.white }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{getCategoryName(cat, lang)}</div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{Object.keys(QUESTION_BANK[cat]).length} {t.subcategoriesLabel}</div>
              </div>
              <div style={{ color: C.textMuted, fontSize: 18 }}>→</div>
            </button>
          ))}
        </div>
      )}

      {tab === 'categories' && selectedCat && !selectedSub && (
        <div>
          <button onClick={() => setSelectedCat(null)} style={{ background: 'none', border: 'none', color: C.textSub, fontSize: 13, cursor: 'pointer', marginBottom: 16 }}>{t.back}</button>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>{getCategoryName(selectedCat, lang)}</div>
          {Object.keys(QUESTION_BANK[selectedCat]).map(sub => (
            <button key={sub} onClick={() => setSelectedSub(sub)} style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '14px 16px', cursor: 'pointer', textAlign: isRTL ? 'right' : 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, color: C.white }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{getSubcategoryName(sub, lang)}</div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{QUESTION_BANK[selectedCat][sub].length} {t.questionsCountLabel}</div>
              </div>
              <div style={{ color: C.textMuted, fontSize: 18 }}>→</div>
            </button>
          ))}
        </div>
      )}

      {tab === 'categories' && selectedCat && selectedSub && (
        <div>
          <button onClick={() => setSelectedSub(null)} style={{ background: 'none', border: 'none', color: C.textSub, fontSize: 13, cursor: 'pointer', marginBottom: 16 }}>{t.back}</button>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{getSubcategoryName(selectedSub, lang)}</div>
          <div style={{ fontSize: 12, color: C.textSub, marginBottom: 16 }}>{QUESTION_BANK[selectedCat][selectedSub].length} {t.questionsAvailable}</div>

          {/* Quick add (random) */}
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '14px 16px', marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.textMuted, marginBottom: 8 }}>{lt.quickAdd}</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {[5, 10, 15, 20].map(n => (
                <button key={n} onClick={() => setHowMany(n)} style={{ flex: 1, background: howMany === n ? C.gold : C.inputBg, color: howMany === n ? '#000' : C.textSub, border: `1px solid ${howMany === n ? C.gold : C.inputBorder}`, borderRadius: 10, padding: '10px 4px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{n}</button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={addFromBank} style={{ flex: 1, background: C.gold, color: '#000', border: 'none', borderRadius: 12, padding: '13px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>➕ {t.addQuestionsBtn} {howMany} {t.questionsWordCap}</button>
              <button onClick={() => {
                const allQs = QUESTION_BANK[selectedCat][selectedSub];
                const shuffled = [...allQs].sort(() => Math.random() - 0.5);
                const picked = shuffled.slice(0, howMany);
                const newQs = [...questions, ...picked];
                updateQuestions(newQs);
                handleStart({ gameName, timer, pts, questions: newQs });
              }} style={{ flex: 1, background: C.red, color: C.white, border: 'none', borderRadius: 12, padding: '13px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>{t.startQuiz}</button>
            </div>
          </div>

          {/* Or pick specific questions */}
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.gold, marginBottom: 10 }}>{lt.pickSpecific}</div>
          {selectAllBar(QUESTION_BANK[selectedCat][selectedSub], pickCat, setPickCat)}
          {QUESTION_BANK[selectedCat][selectedSub].map((item, i) => pickRow(item, i, pickCat, setPickCat))}
          {pickCat.length > 0 && (
            <button onClick={addSelectedCatQuestions} style={{ width: '100%', background: C.green, color: '#000', border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 800, cursor: 'pointer', marginTop: 8 }}>➕ {lt.addSelected} ({pickCat.length})</button>
          )}
        </div>
      )}

      {tab === 'ai' && (
        <div>
          <div style={{ fontSize: 13, color: C.textSub, marginBottom: 20, lineHeight: 1.6 }}>{t.aiDescription}</div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.textMuted, marginBottom: 10 }}>{t.popularTopics}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {['🎵 90s Music', '🍕 Food & Drink', '⚽ Football', '🎬 Movies', '🌍 Geography', '🧠 General Knowledge', '🎭 TV Shows', '🔬 Science', '📚 History', '🏆 Olympics', '🎸 Rock Music', '🍷 Wine'].map(topic => (
                <button key={topic} onClick={() => setAiTopic(topic.slice(3))} style={{ background: aiTopic === topic.slice(3) ? `${C.purple}40` : `${C.purple}12`, border: `1px solid ${aiTopic === topic.slice(3) ? C.purple : `${C.purple}30`}`, borderRadius: 20, padding: '8px 14px', fontSize: 13, color: aiTopic === topic.slice(3) ? C.white : C.textSub, cursor: 'pointer', fontWeight: aiTopic === topic.slice(3) ? 700 : 400 }}>{topic}</button>
              ))}
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.textMuted, marginBottom: 6 }}>{t.orTypeOwn}</div>
            <input type="text" placeholder={t.aiTopicPlaceholder} value={aiTopic} onChange={e => setAiTopic(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.textMuted, marginBottom: 8 }}>{t.howManyLabel}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[5, 10, 15].map(n => (
                <button key={n} onClick={() => setAiCount(n)} style={{ flex: 1, background: aiCount === n ? C.purple : C.inputBg, color: aiCount === n ? '#fff' : C.textSub, border: `1px solid ${aiCount === n ? C.purple : C.inputBorder}`, borderRadius: 10, padding: '10px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{n}</button>
              ))}
            </div>
          </div>
          <button onClick={handleGenerateAI} disabled={!aiTopic.trim()} style={{ width: '100%', background: aiTopic.trim() ? C.purple : 'rgba(255,255,255,0.1)', color: aiTopic.trim() ? '#fff' : C.textMuted, border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 800, cursor: 'pointer', marginBottom: 20 }}>{aiLoading ? t.generating : t.generateQuestions}</button>
          {aiGenerated.length > 0 && !aiLoading && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: C.textMuted, marginBottom: 10 }}>🤖 {aiGenerated.length} {t.questionsGenerated}</div>
              {aiGenerated.map((item, i) => (
                <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 14px', marginBottom: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{getText(item.q, lang)}</div>
                  <div style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>✅ {getText(item.a, lang)}</div>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button onClick={addAIToQuiz} style={{ flex: 1, background: C.gold, color: '#000', border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>{t.addToQuiz}</button>
                <button onClick={() => {
                  const newQs = [...questions, ...aiGenerated];
                  updateQuestions(newQs);
                  handleStart({ gameName, timer, pts, questions: newQs });
                }} style={{ flex: 1, background: C.red, color: C.white, border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>{t.startQuiz}</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default CreateGame;